"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useCapToken from "@core/helpers/client/hooks/useCapToken";
import {
  cap_place_is_on,
  clear_cap_token,
  get_cap_config,
  load_cap_widget,
  register_cap_widget,
  store_cap_token,
} from "@core/helpers/client/captcha";

// The widget draws a small credit label under the checkbox. We show the site's own name there, as plain text.
// It sits in the widget's open shadow root, and the widget only rewrites it when it is empty.
// The widget makes it a link to its own site and keeps resetting that; it is pointed at ours instead.
const BRAND_LABEL = "شاخه آرا";
const BRAND_URL = "https://shakheara.com";

const apply_brand = (widget) => {
  const root = widget.shadowRoot;
  const credits = root?.querySelector(".credits");
  if (!credits) return false;

  if (credits.textContent !== BRAND_LABEL) credits.textContent = BRAND_LABEL;
  credits.setAttribute("aria-label", BRAND_LABEL);
  // only when different: setting the same value again would wake the observer that calls this
  if (credits.getAttribute("href") !== BRAND_URL) credits.setAttribute("href", BRAND_URL);
  if (credits.getAttribute("target") !== "_blank") credits.setAttribute("target", "_blank");
  if (credits.getAttribute("rel") !== "noopener noreferrer") credits.setAttribute("rel", "noopener noreferrer");

  if (!root.querySelector("style[data-brand]")) {
    const style = document.createElement("style");
    style.setAttribute("data-brand", "");
    style.textContent = ".credits{text-decoration:none !important}";
    root.appendChild(style);
    // the widget's own click handler opens its site: take the click over
    root.addEventListener(
      "click",
      (event) => {
        if (event.target?.closest?.(".credits")) {
          event.preventDefault();
          event.stopPropagation();
          window.open(BRAND_URL, "_blank", "noopener,noreferrer");
        }
      },
      true
    );
  }
  return true;
};


// Default look of the floating card: white, grey border, input-sized corners, black check and spinner, the page's
// font. Every --cap-* variable (https://capjs.js.org/guide/widget.html) can be overridden through the `style` prop;
// --cap-host-shadow and --cap-host-radius style the host element itself (none by default: the widget's own card
// has the border, a second frame around it would look doubled).
export const DEFAULT_CAP_STYLE = {
  "--cap-widget-width": "min(280px, calc(100vw - 32px))",
  "--cap-widget-height": "52px",
  "--cap-widget-padding": "12px",
  "--cap-background": "#FFFFFF",
  "--cap-color": "#171717",
  "--cap-border-color": "#D4D4D4",
  "--cap-border-radius": "6px",
  "--cap-font": "inherit",
  "--cap-gap": "10px",
  "--cap-checkbox-size": "20px",
  "--cap-checkbox-border": "1px solid #A3A3A3",
  "--cap-checkbox-border-radius": "4px",
  "--cap-checkbox-background": "#FFFFFF",
  "--cap-checkmark": "#000000",
  "--cap-spinner-color": "#000000",
  "--cap-spinner-background-color": "#E5E5E5",
  "--cap-focus-ring": "0 0 0 2px #D4D4D4",
};

// float_in puts a shadow and radius on the host element inline; this rule (once per page) lets the variables win
const ensure_host_style = () => {
  if (typeof document === "undefined" || document.getElementById("cap-widget-host-style")) return;
  const style = document.createElement("style");
  style.id = "cap-widget-host-style";
  style.textContent =
    "cap-widget{box-shadow:var(--cap-host-shadow,none) !important;border-radius:var(--cap-host-radius,0) !important;background:transparent !important}";
  document.head.appendChild(style);
};

/**
 * The Cap checkbox, for a site's login / register / change-mobile form. It renders nothing unless the site has the
 * "CAP" service (and has not switched the captcha off for `place`), so it can be placed unconditionally. The auth
 * hooks (useAuth, useAuthWithEmailMobile) pick its token up by themselves.
 *
 * It takes no room in the form: the widget floats over the page, next to the spot where this component sits, while
 * the visitor's submit solves it, and goes away afterwards. `place` ("login", "register", "change-mobile",
 * "change-email" or a list) is optional; without it the widget is there when any place is on.
 * Theme it with the --cap-* CSS variables (https://capjs.js.org/guide/widget.html) through `style`.
 */
const CapCaptcha = ({ place, style }) => {
  const { captcha_enabled } = useCapToken();
  const widget_ref = useRef(null);
  const anchor_ref = useRef(null);
  const [endpoint, set_endpoint] = useState(null);
  const place_key = [].concat(place ?? []).join("|");

  useEffect(() => {
    if (!captcha_enabled) return;
    ensure_host_style();

    let cancelled = false;
    (async () => {
      try {
        const [config] = await Promise.all([
          get_cap_config(),
          load_cap_widget().then(() => customElements.whenDefined("cap-widget")),
        ]);
        if (!cancelled) set_endpoint(config && cap_place_is_on(config.places, place) ? config.api_endpoint : null);
      } catch {
        // without the widget the hook falls back to the background solve, and the server decides
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captcha_enabled, place_key]);

  useEffect(() => {
    const widget = widget_ref.current;
    if (!widget) return;

    const on_solve = (event) => store_cap_token(event.detail?.token);
    const on_clear = () => clear_cap_token();

    widget.addEventListener("solve", on_solve);
    widget.addEventListener("reset", on_clear);
    widget.addEventListener("error", on_clear);
    const unregister = register_cap_widget(widget);
    return () => {
      widget.removeEventListener("solve", on_solve);
      widget.removeEventListener("reset", on_clear);
      widget.removeEventListener("error", on_clear);
      unregister();
      clear_cap_token();
    };
  }, [endpoint]);

  useEffect(() => {
    const widget = widget_ref.current;
    if (!widget) return;

    let observer = null;
    let tries = 0;
    const attach = () => {
      if (apply_brand(widget)) {
        observer = new MutationObserver(() => apply_brand(widget));
        observer.observe(widget.shadowRoot, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["href"] });
      } else if (tries++ < 40) {
        timer = setTimeout(attach, 50);
      }
    };
    let timer = setTimeout(attach, 0);

    return () => {
      clearTimeout(timer);
      observer?.disconnect();
    };
  }, [endpoint]);

  if (!captcha_enabled) return null;

  return (
    <>
      <span ref={anchor_ref} aria-hidden="true" style={{ display: "block", height: 0 }} />
      {endpoint &&
        createPortal(
          <cap-widget
            ref={(element) => {
              widget_ref.current = element;
              // where the widget floats in: next to this component's anchor
              if (element) element._cap_anchor = anchor_ref;
            }}
            data-cap-api-endpoint={endpoint}
            data-cap-lang="fa"
            data-cap-i18n-initial-state="تأیید کنید که انسان هستید"
            data-cap-i18n-verifying-label="در حال تأیید..."
            data-cap-i18n-solved-label="شما انسان هستید"
            data-cap-i18n-error-label="خطا. دوباره تلاش کنید."
            style={{ display: "none", position: "fixed", "--cap-widget-width": "min(300px, calc(100vw - 16px))", ...DEFAULT_CAP_STYLE, ...style }}
          />,
          document.body
        )}
    </>
  );
};

export default CapCaptcha;
